import { create } from "zustand";
import { PackageLookup } from "./PackageLookup";
import { CapturePackagePhotos } from "./CapturePackagePhotos";
import { CheckContents } from "./CheckContents";
import { ControlWeightCapture } from "./ControlWeightCapture";
import { PackagingWeightCapture } from "./PackagingWeightCapture";
import { ContentWeightCapture } from "./ContentWeightCapture";
import { NonConformPhoto } from "./NonConformPhoto";
import { SortContents } from "./SortContents";
import { AssembledWeightCapture } from "./AssembledWeightCapture";
import { ConformingWeightCapture } from "./ConformingWeightCapture";
import { CaptureScaleReadingPhoto } from "./CaptureScaleReadingPhoto";
import { Summary } from "./Summary";
import { RejectContents } from "./RejectContents";
import { ProcessingIssues } from "./ProcessingIssues";
import { AllSteps } from "./AllSteps";
import { AutoEscalated } from "./AutoEscalated";
import { RejectionConfirmation } from "./RejectionConfirmation";
import { ProcessingConfirmation } from "./ProcessingConfirmation";
import { ReturnToSellerInfo } from "./ReturnToSellerInfo";

import { isStepValid, isStepVisible } from "./useConfiguration";
import { useWarehouse } from "./useWarehouse";

const steps = [
  PackageLookup,
  CapturePackagePhotos,
  CheckContents,
  ControlWeightCapture,
  PackagingWeightCapture,
  ContentWeightCapture,
  SortContents,
  NonConformPhoto,
  AssembledWeightCapture,
  ConformingWeightCapture,
  CaptureScaleReadingPhoto,
  Summary,
  ProcessingIssues,
];

export const useSteps = create((set, get) => ({
  currentStep: 0,
  StepComponent: steps[0],
  hasNext: steps.length > 1,
  hasPrevious: false,
  props: {},
  checkInSteps: [],
  setCheckInSteps: (steps) => set({ checkInSteps: steps }),
  stepIndex: 0,
  setStepIndex: (index) => set({ stepIndex: index }),
  isProcessingCheckIn: false,
  checkInCallback: null,
  previousStepBeforeAllSteps: null,
  previousStepIndexBeforeAllSteps: null,
  dropStoreMode: (() => {
    // Initialize from localStorage
    const stored = localStorage.getItem("dropStoreMode");
    return stored === "true";
  })(),
  setDropStoreMode: (value) => {
    localStorage.setItem("dropStoreMode", value.toString());
    set({ dropStoreMode: value });
  },

  next: () => {
    const state = get();

    // If we're processing check-in steps, use nextCheckInStep instead
    if (state.isProcessingCheckIn) {
      get().nextCheckInStep();
      return;
    }

    set((state) => {
      const newStep = steps.findIndex(
        (step, index) => index > state.currentStep && isStepVisible(step)
      );
      const newState = {
        currentStep: newStep,
        StepComponent: steps[newStep],
        hasNext: newStep < steps.length - 1,
        hasPrevious: newStep > 0,
      };
      return newState;
    });
  },

  nextNonValid: () =>
    set((state) => {
      const newStep = steps.findIndex(
        (step) => isStepVisible(step) && !isStepValid(step)
      );
      const newState = {
        currentStep: newStep,
        StepComponent: steps[newStep],
        hasNext: newStep < steps.length - 1,
        hasPrevious: newStep > 0,
      };
      return newState;
    }),

  previous: () =>
    set((state) => {
      const newStep = steps.findLastIndex(
        (step, index) => index < state.currentStep && isStepVisible(step)
      );
      if (newStep === -1) return state;
      const newState = {
        currentStep: newStep,
        StepComponent: steps[newStep],
        hasNext: newStep < steps.length - 1,
        hasPrevious: newStep > 0,
      };
      return newState;
    }),

  exit: (actionProps) =>
    set(() => ({
      currentStep: -1,
      StepComponent: null,
      hasNext: true,
      hasPrevious: false,
      props: actionProps,
    })),

  clear: () => set((state) => ({ ...state, props: {} })),

  reject: () => set((state) => ({ ...state, StepComponent: RejectContents })),

  cancelReject: () =>
    set((state) => ({ ...state, StepComponent: steps[state.currentStep] })),

  openAllSteps: () =>
    set((state) => ({
      ...state,
      StepComponent: AllSteps,
      previousStepBeforeAllSteps: state.StepComponent,
      previousStepIndexBeforeAllSteps: state.currentStep,
    })),

  closeAllSteps: () =>
    set((state) => {
      // If we have a saved previous step, return to it
      if (state.previousStepBeforeAllSteps) {
        return {
          ...state,
          StepComponent: state.previousStepBeforeAllSteps,
          currentStep: state.previousStepIndexBeforeAllSteps,
          previousStepBeforeAllSteps: null,
          previousStepIndexBeforeAllSteps: null,
        };
      }
      // Fallback to Summary as requested
      return { ...state, StepComponent: Summary };
    }),

  showEscalated: () => set(() => ({ StepComponent: AutoEscalated })),

  showRejection: () =>
    set((state) => ({ ...state, StepComponent: RejectionConfirmation })),

  showProcessingConfirmation: () =>
    set((state) => ({ ...state, StepComponent: ProcessingConfirmation })),

  showReturnToSellerInfo: (selectedOption, description) =>
    set((state) => ({
      ...state,
      StepComponent: ReturnToSellerInfo,
      props: { selectedOption, description },
    })),

  closeLogIssue: (actionProps) =>
    set((state) => ({
      ...state,
      StepComponent: steps[state.currentStep],
      props: actionProps,
    })),

  jumpTo: (StepComponentToJump) =>
    set((state) => {
      const newStep = steps.findIndex((step) => step === StepComponentToJump);
      const newState = {
        ...state,
        StepComponent: steps[newStep],
        currentStep: newStep,
        hasNext: newStep < steps.length - 1,
        hasPrevious: newStep > 0,
      };
      return newState;
    }),

  hasMissingOrInvalidData: () => {
    const badStep = steps.find(
      (step) => isStepVisible(step) && !isStepValid(step)
    );
    return !!badStep;
  },

  processCheckInSteps: async (requiredSteps, onComplete) => {
    // Set up the check-in process state
    set({
      checkInSteps: requiredSteps,
      stepIndex: 0,
      isProcessingCheckIn: true,
      checkInCallback: onComplete,
    });

    // Show the first step
    if (requiredSteps.length > 0) {
      set({ StepComponent: requiredSteps[0] });
    }
  },

  nextCheckInStep: () => {
    const state = get();
    const { checkInSteps, stepIndex, checkInCallback } = state;

    const nextIndex = stepIndex + 1;

    if (nextIndex < checkInSteps.length) {
      // Move to next step
      set({
        stepIndex: nextIndex,
        StepComponent: checkInSteps[nextIndex],
      });
    } else {
      // All steps completed
      set({
        checkInSteps: [],
        stepIndex: 0,
        isProcessingCheckIn: false,
        checkInCallback: null,
        StepComponent: steps[0], // Return to PackageLookup
      });

      // Call the completion callback
      if (checkInCallback) {
        checkInCallback();
      }
    }
  },

  resetToPackageLookup: () => {
    // Reset warehouse state
    useWarehouse.getState().reset();

    // Reset steps state
    set({
      currentStep: 0,
      StepComponent: steps[0],
      hasNext: steps.length > 1,
      hasPrevious: false,
      props: {},
      checkInSteps: [],
      stepIndex: 0,
      isProcessingCheckIn: false,
      checkInCallback: null,
    });
  },
}));
